import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'

import { useGetHomepageConfig, useUpdateHomepageConfig } from '@/api/building-dashboard/home-screen'
import { Page } from '@/components/layout/page'
import { PageHeader } from '@/components/layout/page-header'
import { EmptyState } from '@/components/ui/empty-state'
import { Grid } from '@/components/ui/grid'
import { ToggleSwitch } from '@/components/ui/toggle-switch'
import { toastFactory } from '@/factories'
import { useDesignVersionToggle } from '@/hooks'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { getApiErrorMessage } from '@/utils/error'

import {
  BLOCKS_GRID_COLUMNS,
  getShortcutLabels,
  getWidgetLabels,
  MAX_ENABLED_SHORTCUTS,
} from './home-screen.constants'
import * as S from './home-screen.styled'
import { HomepageConfigResponse, HomepageWidgetType, ShortcutType } from './home-screen.types'
import {
  applyShortcutOrder,
  applyWidgetOrder,
  buildInitialShortcutOrder,
  buildInitialWidgetOrder,
  buildSavePayload,
  moveInOrder,
  normalizeWidgetOrder,
  reorderServices,
  sortByPosition,
  toggleService,
  toggleShortcutEnabled,
  toggleWidgetEnabled,
} from './home-screen.utils'
import { HomeScreenSkeleton } from './home-screen-skeleton'
import { ServicesBlock } from './services-block/services-block'
import { ShortcutsBlock } from './shortcuts-block/shortcuts-block'
import { WidgetsBlock } from './widgets-block/widgets-block'

export const HomeScreenPage = () => {
  const { t } = useTranslation(['home-screen'])
  const navigate = useNavigate()
  const { buildingId: buildingIdParam } = useParams<{ buildingId: string }>()
  const buildingId = buildingIdParam ? Number(buildingIdParam) : undefined
  const activeBuilding = useSelector(selectActiveBuilding)

  const WIDGET_LABELS = getWidgetLabels(t)
  const SHORTCUT_LABELS = getShortcutLabels(t)

  const { data: config, isLoading } = useGetHomepageConfig({ buildingId })
  const { mutateAsync: updateConfig, isPending: isSaving } = useUpdateHomepageConfig()

  // The backend only persists enabled entries' order, so the full display order (active +
  // inactive) lives here instead, re-seeded only when the building changes.
  const [widgetOrder, setWidgetOrder] = useState<HomepageWidgetType[] | null>(null)
  const [shortcutOrder, setShortcutOrder] = useState<ShortcutType[] | null>(null)

  const { redirect, toggle: handleUIToggle } = useDesignVersionToggle({
    group: 'home-screen',
    screen: 'HomeScreen',
    to: {
      params: { buildingId: buildingId ?? activeBuilding?.id ?? '' },
      path: '/dashboard/building-dashboard/home-screen/:buildingId/legacy',
    },
    version: 'new',
  })

  useEffect(() => {
    if (activeBuilding?.id && buildingId !== activeBuilding.id) {
      navigate(`/dashboard/building-dashboard/home-screen/${activeBuilding.id}`, { replace: true })
    }
  }, [activeBuilding, buildingId, navigate])

  useEffect(() => {
    setWidgetOrder(null)
    setShortcutOrder(null)
  }, [buildingId])

  useEffect(() => {
    if (config && !widgetOrder) setWidgetOrder(buildInitialWidgetOrder(config))
    if (config && !shortcutOrder) setShortcutOrder(buildInitialShortcutOrder(config))
  }, [config, widgetOrder, shortcutOrder])

  if (redirect) return redirect

  const persist = async (updated: HomepageConfigResponse, successMessage: string) => {
    if (!buildingId) return
    try {
      await updateConfig(buildSavePayload(buildingId, updated))
      toastFactory.success(successMessage)
    } catch (error) {
      toastFactory.error(getApiErrorMessage(error))
    }
  }

  const handleToggleWidget = (widget: HomepageWidgetType) => {
    if (!config || !widgetOrder) return
    const toggled = toggleWidgetEnabled(config, widget)
    const nextOrder = normalizeWidgetOrder(widgetOrder, toggled)
    if (nextOrder !== widgetOrder) setWidgetOrder(nextOrder)
    persist(applyWidgetOrder(toggled, nextOrder), t('home-screen:messages.widgetUpdated'))
  }

  const handleReorderWidgets = (sourceIndex: number, targetIndex: number) => {
    if (!config || !widgetOrder) return
    const reordered = moveInOrder(widgetOrder, sourceIndex, targetIndex)
    if (!reordered) return
    setWidgetOrder(reordered)
    persist(applyWidgetOrder(config, reordered), t('home-screen:messages.widgetOrderUpdated'))
  }

  // shortcutsEnabled/servicesEnabled gate these two lists in the UI (grayed out, drag/toggle
  // disabled - see ShortcutsBlock/ServicesBlock's `locked` prop); re-checked here too so a
  // request can never be sent for either list while its parent widget is off, even if the UI
  // state was bypassed.
  const handleToggleShortcut = (type: ShortcutType) => {
    if (!config || !shortcutOrder || !config.shortcutsEnabled) return
    const { config: toggled, blocked } = toggleShortcutEnabled(config, type)
    if (blocked) {
      toastFactory.error(t('home-screen:messages.shortcutsCapHint', { max: MAX_ENABLED_SHORTCUTS }))
      return
    }
    persist(applyShortcutOrder(toggled, shortcutOrder), t('home-screen:messages.shortcutUpdated'))
  }

  const handleReorderShortcuts = (sourceIndex: number, targetIndex: number) => {
    if (!config || !shortcutOrder || !config.shortcutsEnabled) return
    const reordered = moveInOrder(shortcutOrder, sourceIndex, targetIndex)
    if (!reordered) return
    setShortcutOrder(reordered)
    persist(applyShortcutOrder(config, reordered), t('home-screen:messages.shortcutOrderUpdated'))
  }

  // TODO: BE only stores enabled services, so a service turned off disappears from this list
  // and can't be turned back on here. Needs BE to return the building's available booking types
  // (or keep disabled ones) so off services stay listed, unchecked - like shortcuts.
  const handleToggleService = (bookingType: number) => {
    if (!config || !config.servicesEnabled) return
    persist(toggleService(config, bookingType), t('home-screen:messages.serviceUpdated'))
  }

  const handleReorderServices = (sourceIndex: number, targetIndex: number) => {
    if (!config || !config.servicesEnabled) return
    persist(
      reorderServices(config, sourceIndex, targetIndex),
      t('home-screen:messages.serviceOrderUpdated'),
    )
  }

  const disabled = isSaving

  const renderContent = () => {
    if (!isLoading && !config) {
      return <EmptyState description="" title={t('home-screen:labels.noConfigMessage')} />
    }
    if (!config || !widgetOrder || !shortcutOrder) return <HomeScreenSkeleton />

    return (
      <S.ContentCard className="ctp-home-screen">
        <Grid columns={BLOCKS_GRID_COLUMNS} gap={20}>
          <WidgetsBlock
            config={config}
            disabled={disabled}
            order={widgetOrder}
            widgetLabels={WIDGET_LABELS}
            onReorder={handleReorderWidgets}
            onToggle={handleToggleWidget}
          />
          <ShortcutsBlock
            config={config}
            disabled={disabled}
            locked={!config.shortcutsEnabled}
            order={shortcutOrder}
            shortcutLabels={SHORTCUT_LABELS}
            onReorder={handleReorderShortcuts}
            onToggle={handleToggleShortcut}
          />
          <ServicesBlock
            disabled={disabled}
            locked={!config.servicesEnabled}
            services={sortByPosition(config.services)}
            onReorder={handleReorderServices}
            onToggle={handleToggleService}
          />
        </Grid>
      </S.ContentCard>
    )
  }

  return (
    <Page centered>
      <PageHeader
        actions={
          <ToggleSwitch
            checked
            falsyLabel={t('home-screen:page.designToggle.old')}
            truthyLabel={t('home-screen:page.designToggle.new')}
            onChange={handleUIToggle}
          />
        }
        title={t('home-screen:page.title')}
      />

      {renderContent()}
    </Page>
  )
}
